import React from 'react';
import { ArrayFormField } from '../typings';
import { Checkbox } from './inputs';
import { Button, Icon } from '@/ui-kit';
import './ArrayAdd.scss';

type ArrayAddProps = {
  fieldsArray: ArrayFormField;
  disabled?: boolean;
  visible: boolean;
  /** No entry is on screen, so the group's title shows here instead. */
  collapsed?: boolean;
  isFieldNA: boolean;
  /** The entry shows the Not Applicable box under its title instead. */
  notApplicableOnTop?: boolean;
  onAddItem: (fieldId: string) => () => void;
  onNotApplicableChange?: () => void;
};

export const ArrayAdd: React.FC<ArrayAddProps> = ({
  fieldsArray,
  disabled,
  visible,
  collapsed,
  isFieldNA,
  notApplicableOnTop,
  onAddItem,
  onNotApplicableChange,
}) => {
  // Only rendered on the last entry, so index 0 means it is the only one; beside other
  // entries the box would mark just this one. A ticked box stays so it can be unticked.
  const notApplicableField =
    !notApplicableOnTop && (fieldsArray.index === 0 || isFieldNA)
      ? fieldsArray.notApplicableField
      : undefined;

  const label =
    fieldsArray.questionIfOptional ||
    (collapsed ? fieldsArray.title : undefined);

  // The bar sits under the last entry, so that entry's position counts the entries on screen.
  // `!= null`, not `!== undefined`: a null maximum must never read as 0 and hide Add.
  const full =
    !collapsed &&
    fieldsArray.maxItems != null &&
    fieldsArray.index + 1 >= fieldsArray.maxItems;

  // A full group keeps only the Not Applicable box it offers beside its question.
  if (!visible || (full && notApplicableField === undefined)) return null;

  return (
    <div className="array-add__container">
      {(label || fieldsArray.hint || notApplicableField !== undefined) && (
        <div className="array-add__question-info">
          <span className="array-add__question-label">{label}</span>
          <span className="array-add__question-hint">{fieldsArray.hint}</span>
          {notApplicableField !== undefined && (
            <div style={{ marginTop: '10px' }}>
              <Checkbox
                name={notApplicableField.fieldId}
                label={notApplicableField.label}
                size="medium"
                onChange={onNotApplicableChange}
              />
            </div>
          )}
        </div>
      )}

      {!full && (
        <div className="array-add__action">
          <Button
            leadingIcon={<Icon type="plus" color="var(--color_shadow-dark)" />}
            variant="secondary"
            kind="muted"
            size="small"
            type="button"
            onClick={onAddItem(fieldsArray.fieldId)}
            disabled={isFieldNA || disabled}
          >
            Add {fieldsArray.title}
          </Button>
        </div>
      )}
    </div>
  );
};
